import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Bell, Mail, AlertTriangle, Clock, Activity, RefreshCw,
  Plus, X, ExternalLink, Save, ArrowRight, FileText, TrendingUp, Users
} from 'lucide-react';
import RecipientsPanel from '../components/notifications/RecipientsPanel';
import DiagnosticPanel from '../components/notifications/DiagnosticPanel';
import EmailLogPanel from '../components/notifications/EmailLogPanel';

/**
 * v5.4.16 — Notifications Configuration Hub
 *
 * Central place where admins configure all alert pipelines:
 *  - Inactivity alert (full inline config)
 *  - Shortcuts to other existing notification configs (Bomberazo, Loans,
 *    Lost/Overdue, KPI Thresholds, Transition Alerts) that still live in
 *    their original screens. These cards show the current ON/OFF state plus
 *    a "Configurar" button that navigates to the right page.
 */
export default function NotificationsConfigPage() {
  const { api } = useAuth();
  const navigate = useNavigate();

  // --- Inactivity alert state ---
  const [inact, setInact] = useState(null);
  const [inactLoading, setInactLoading] = useState(true);
  const [inactSaving, setInactSaving] = useState(false);
  const [inactTesting, setInactTesting] = useState(false);
  const [newEmail, setNewEmail] = useState('');

  const loadInactivity = useCallback(async () => {
    setInactLoading(true);
    try {
      const res = await api().get('/notifications/inactivity-alert');
      setInact(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar la config de inactividad');
    } finally {
      setInactLoading(false);
    }
  }, [api]);

  useEffect(() => { loadInactivity(); }, [loadInactivity]);

  const saveInactivity = async () => {
    setInactSaving(true);
    try {
      const res = await api().put('/notifications/inactivity-alert', {
        enabled: inact.enabled,
        threshold_days: inact.threshold_days,
        escalate_threshold_days: inact.escalate_threshold_days,
        extra_emails: inact.extra_emails,
      });
      toast.success('Configuración de inactividad guardada');
      setInact((s) => ({ ...s, ...res.data.value }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setInactSaving(false);
    }
  };

  const testInactivity = async () => {
    setInactTesting(true);
    try {
      await api().post('/notifications/inactivity-alert/test');
      toast.success('Alerta de prueba enviada. Revisa tu correo.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo disparar la prueba');
    } finally {
      setInactTesting(false);
    }
  };

  const addEmail = () => {
    const v = newEmail.trim();
    if (!v || !v.includes('@')) { toast.error('Correo inválido'); return; }
    if (inact.extra_emails?.includes(v)) { toast.error('Ya está agregado'); return; }
    setInact((s) => ({ ...s, extra_emails: [...(s.extra_emails || []), v] }));
    setNewEmail('');
  };

  const removeEmail = (em) => {
    setInact((s) => ({ ...s, extra_emails: (s.extra_emails || []).filter((e) => e !== em) }));
  };

  // v5.4.17 — Send sample Lost-Overdue PDF to validate the redesigned layout
  const [lostPdfTesting, setLostPdfTesting] = useState(false);
  const sendTestLostOverduePdf = async () => {
    const email = window.prompt(
      'Probar PDF de Equipos Perdidos (rediseño v5.4.17)\n\n' +
      'Escribe el correo donde quieres recibir el adjunto de prueba:',
      ''
    );
    if (!email || !email.trim()) return;
    setLostPdfTesting(true);
    try {
      const res = await api().post('/email/test-lost-overdue-pdf', {
        recipient_email: email.trim()
      });
      toast.success(
        `PDF enviado a ${res.data.recipient} vía ${res.data.provider}. ` +
        `Revisa tu bandeja en 1-2 minutos.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar el PDF de prueba');
    } finally {
      setLostPdfTesting(false);
    }
  };

  // v5.4.18 — Inline configs for ALL notification categories
  // Each config: { endpoint, payload_shape, label }
  const [trans, setTrans] = useState(null);
  const [transSaving, setTransSaving] = useState(false);
  const [loan, setLoan] = useState(null);
  const [loanSaving, setLoanSaving] = useState(false);
  const [lost, setLost] = useState(null);
  const [lostSaving, setLostSaving] = useState(false);
  const [kpi, setKpi] = useState(null);
  const [kpiSaving, setKpiSaving] = useState(false);
  const [bomberazo, setBomberazo] = useState(null);
  const [bomberazoSaving, setBomberazoSaving] = useState(false);
  // v5.4.21 — Orphan units alert config
  const [orphan, setOrphan] = useState(null);
  const [orphanSaving, setOrphanSaving] = useState(false);
  const [newOrphanEmail, setNewOrphanEmail] = useState('');
  // v5.4.22 — Weekly briefing config
  const [briefing, setBriefing] = useState(null);
  const [briefingSaving, setBriefingSaving] = useState(false);
  const [briefingSending, setBriefingSending] = useState(false);
  const [newBriefingEmail, setNewBriefingEmail] = useState('');
  // v5.4.26 — Executive report weekly schedule
  const [execReport, setExecReport] = useState(null);
  const [execReportSaving, setExecReportSaving] = useState(false);
  const [execReportSending, setExecReportSending] = useState(false);
  const [newExecRecipient, setNewExecRecipient] = useState('');
  // v5.4.27 — Return reminders + Executive inactivity threshold
  const [returnReminder, setReturnReminder] = useState(null);
  const [returnReminderSaving, setReturnReminderSaving] = useState(false);
  const [returnReminderRunning, setReturnReminderRunning] = useState(false);
  const [inactivityCfg, setInactivityCfg] = useState(null);
  const [inactivitySaving, setInactivitySaving] = useState(false);

  const loadAllConfigs = useCallback(async () => {
    try {
      const [t, l, lo, k, b, o, br, er, rr, ia] = await Promise.all([
        api().get('/config/movement-transition-alerts').catch(() => null),
        api().get('/config/return-alerts').catch(() => null),
        api().get('/config/lost-overdue-alerts').catch(() => null),
        api().get('/config/kpi-threshold-alerts').catch(() => null),
        api().get('/bomberazo-alerts/config').catch(() => null),
        api().get('/config/orphan-units-alerts').catch(() => null),
        api().get('/briefing/config').catch(() => null),
        api().get('/config/executive-report').catch(() => null),
        api().get('/config/return-reminders').catch(() => null),
        api().get('/config/executive-inactivity').catch(() => null),
      ]);
      if (t?.data) setTrans(t.data);
      if (l?.data) setLoan(l.data);
      if (lo?.data) setLost(lo.data);
      if (k?.data) setKpi(k.data);
      if (b?.data) setBomberazo(b.data);
      if (o?.data) setOrphan(o.data);
      if (br?.data) setBriefing(br.data);
      if (er?.data) setExecReport(er.data);
      if (rr?.data) setReturnReminder(rr.data);
      if (ia?.data) setInactivityCfg(ia.data);
    } catch (e) {
      console.error('Error loading notification configs', e);
    }
  }, [api]);
  useEffect(() => { loadAllConfigs(); }, [loadAllConfigs]);

  // ----- Return reminders (v5.4.27) -----
  const saveReturnReminder = async (patch) => {
    setReturnReminderSaving(true);
    try {
      const next = { ...(returnReminder || {}), ...patch };
      await api().put('/config/return-reminders', next);
      setReturnReminder(next);
      toast.success('Recordatorio de retorno actualizado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setReturnReminderSaving(false);
    }
  };

  const runReturnRemindersNow = async () => {
    setReturnReminderRunning(true);
    try {
      const res = await api().post('/config/return-reminders/run-now');
      toast.success(res?.data?.message || 'Escaneo ejecutado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al ejecutar');
    } finally {
      setReturnReminderRunning(false);
    }
  };

  // ----- Executive inactivity threshold (v5.4.27) -----
  const saveExecInactivity = async (patch) => {
    setInactivitySaving(true);
    try {
      const next = { ...(inactivityCfg || {}), ...patch };
      await api().put('/config/executive-inactivity', next);
      setInactivityCfg(next);
      toast.success('Umbral de inactividad actualizado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setInactivitySaving(false);
    }
  };

  // ----- Executive report scheduled email (v5.4.26) -----
  const saveExecReport = async (patch) => {
    setExecReportSaving(true);
    try {
      const next = { ...(execReport || {}), ...patch };
      await api().put('/config/executive-report', next);
      setExecReport(next);
      toast.success('Reporte Ejecutivo actualizado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setExecReportSaving(false);
    }
  };

  const sendExecReportNow = async () => {
    setExecReportSending(true);
    try {
      const res = await api().post('/config/executive-report/send-now');
      const data = res?.data || {};
      if (data.sent) {
        toast.success(`Reporte enviado a ${data.recipients?.length || 0} destinatario(s)`);
      } else if (data.skipped) {
        toast.warning(`Envío omitido: ${data.reason || 'configuración incompleta'}`);
      } else {
        toast.error(data.error || 'No se pudo enviar el reporte');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al enviar el reporte');
    } finally {
      setExecReportSending(false);
    }
  };

  const addExecRecipient = () => {
    const email = (newExecRecipient || '').trim();
    if (!email || !email.includes('@')) {
      toast.error('Correo inválido');
      return;
    }
    const curr = execReport?.recipients || [];
    if (curr.includes(email)) {
      toast.warning('Ya está en la lista');
      return;
    }
    saveExecReport({ recipients: [...curr, email] });
    setNewExecRecipient('');
  };

  const removeExecRecipient = (email) => {
    const curr = execReport?.recipients || [];
    saveExecReport({ recipients: curr.filter(e => e !== email) });
  };

  // ----- Transition alerts -----
  const saveTransitions = async (patch) => {
    setTransSaving(true);
    try {
      const next = { ...trans, ...patch };
      await api().put('/config/movement-transition-alerts', next);
      setTrans(next);
      toast.success('Notificaciones de movimientos actualizadas');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setTransSaving(false);
    }
  };

  // ----- Return alerts (Equipos pendientes de retorno) -----
  const saveLoan = async (patch) => {
    setLoanSaving(true);
    try {
      const next = { ...loan, ...patch };
      await api().put('/config/return-alerts', next);
      setLoan(next);
      toast.success('Alertas de retorno actualizadas');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setLoanSaving(false);
    }
  };

  // ----- Lost overdue -----
  const saveLost = async (patch) => {
    setLostSaving(true);
    try {
      const next = { ...lost, ...patch };
      await api().put('/config/lost-overdue-alerts', next);
      setLost(next);
      toast.success('Alertas de equipo perdido actualizadas');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setLostSaving(false);
    }
  };

  // ----- KPI -----
  const saveKpi = async (patch) => {
    setKpiSaving(true);
    try {
      const next = { ...kpi, ...patch };
      await api().put('/config/kpi-threshold-alerts', next);
      setKpi(next);
      toast.success('Alertas KPI actualizadas');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setKpiSaving(false);
    }
  };

  // ----- Bomberazo (POST not PUT in this endpoint) -----
  const saveBomberazo = async (patch) => {
    setBomberazoSaving(true);
    try {
      const next = { ...bomberazo, ...patch };
      await api().post('/bomberazo-alerts/config', next);
      setBomberazo(next);
      toast.success('Alertas de bomberazo actualizadas');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setBomberazoSaving(false);
    }
  };

  // v5.4.21 — Orphan units
  const saveOrphan = async (patch) => {
    setOrphanSaving(true);
    try {
      const next = { ...orphan, ...patch };
      await api().put('/config/orphan-units-alerts', next);
      setOrphan(next);
      toast.success('Alertas de unidades huérfanas actualizadas');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setOrphanSaving(false);
    }
  };
  const addOrphanEmail = () => {
    const em = (newOrphanEmail || '').trim();
    if (!em || !em.includes('@')) {
      toast.error('Correo inválido');
      return;
    }
    const next = [...(orphan?.extra_emails || []), em];
    setNewOrphanEmail('');
    saveOrphan({ extra_emails: next });
  };
  const removeOrphanEmail = (em) => {
    const next = (orphan?.extra_emails || []).filter((e) => e !== em);
    saveOrphan({ extra_emails: next });
  };

  // v5.4.22 — Weekly briefing
  const saveBriefing = async (patch) => {
    setBriefingSaving(true);
    try {
      const next = { ...briefing, ...patch };
      await api().put('/briefing/config', next);
      setBriefing(next);
      toast.success('Briefing semanal actualizado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setBriefingSaving(false);
    }
  };
  const addBriefingEmail = () => {
    const em = (newBriefingEmail || '').trim();
    if (!em || !em.includes('@')) {
      toast.error('Correo inválido');
      return;
    }
    const current = briefing?.extra_recipients || [];
    if (current.includes(em)) return;
    setNewBriefingEmail('');
    saveBriefing({ extra_recipients: [...current, em] });
  };
  const removeBriefingEmail = (em) => {
    const next = (briefing?.extra_recipients || []).filter((e) => e !== em);
    saveBriefing({ extra_recipients: next });
  };
  const sendBriefingNow = async () => {
    setBriefingSending(true);
    try {
      const r = await api().post('/briefing/send-now');
      toast.success(r.data?.message || 'Briefing enviado');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar');
    } finally {
      setBriefingSending(false);
    }
  };


  return (
    <div className="container mx-auto p-4 lg:p-6 space-y-4" data-testid="notifications-config-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Bell className="h-6 w-6" />
              Centro de Notificaciones
            </h1>
            <p className="text-sm text-muted-foreground">
              Configura todas las alertas automáticas del sistema y a quién deben llegar.
            </p>
          </div>
        </div>

        {/* v5.4.19 — Tabs to reduce scroll */}
        <Tabs defaultValue="destinatarios" className="w-full" data-testid="notifications-tabs">
          <TabsList className="flex flex-wrap h-auto gap-1 justify-start">
            <TabsTrigger value="destinatarios" data-testid="tab-destinatarios">
              <Users className="h-4 w-4 mr-1.5" /> Destinatarios
            </TabsTrigger>
            <TabsTrigger value="diagnostico" data-testid="tab-diagnostico">
              <Activity className="h-4 w-4 mr-1.5" /> Diagnóstico
            </TabsTrigger>
            <TabsTrigger value="historial" data-testid="tab-historial">
              <Mail className="h-4 w-4 mr-1.5" /> Historial de envíos
            </TabsTrigger>
            <TabsTrigger value="movimientos" data-testid="tab-movimientos">
              <Bell className="h-4 w-4 mr-1.5" /> Movimientos
            </TabsTrigger>
            <TabsTrigger value="retornos" data-testid="tab-retornos">
              <Clock className="h-4 w-4 mr-1.5" /> Retornos
            </TabsTrigger>
            <TabsTrigger value="perdidos" data-testid="tab-perdidos">
              <AlertTriangle className="h-4 w-4 mr-1.5" /> Perdidos
            </TabsTrigger>
            <TabsTrigger value="bomberazos" data-testid="tab-bomberazos">
              <AlertTriangle className="h-4 w-4 mr-1.5" /> Bomberazos
            </TabsTrigger>
            <TabsTrigger value="kpi" data-testid="tab-kpi">
              <Activity className="h-4 w-4 mr-1.5" /> KPI
            </TabsTrigger>
            <TabsTrigger value="huerfanas" data-testid="tab-huerfanas">
              <AlertTriangle className="h-4 w-4 mr-1.5" /> Huérfanas
            </TabsTrigger>
            <TabsTrigger value="briefing" data-testid="tab-briefing">
              <FileText className="h-4 w-4 mr-1.5" /> Briefing
            </TabsTrigger>
            <TabsTrigger value="inactividad" data-testid="tab-inactividad">
              <Activity className="h-4 w-4 mr-1.5" /> Inactividad
            </TabsTrigger>
            <TabsTrigger value="ejecutivo" data-testid="tab-ejecutivo">
              <TrendingUp className="h-4 w-4 mr-1.5" /> Ejecutivo
            </TabsTrigger>
            <TabsTrigger value="otros" data-testid="tab-otros">
              <FileText className="h-4 w-4 mr-1.5" /> Otros
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inactividad" className="mt-4">
        {/* Inactivity Alert */}
        <Card className="border-amber-500/40" data-testid="inactivity-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="h-5 w-5 text-amber-500" />
              Alerta de Inactividad
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Notifica a los admins (y a los correos externos configurados) cuando no se ha registrado
              ningún movimiento (salida, retorno o préstamo) durante N días. Útil para detectar que el
              equipo operativo no está usando el sistema.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {inactLoading || !inact ? (
              <div className="text-sm text-muted-foreground">Cargando...</div>
            ) : (
              <>
                {/* Status banner */}
                <div className={`p-3 rounded-lg border ${
                  inact.days_since_last_movement == null
                    ? 'border-slate-500/30 bg-slate-500/5'
                    : inact.days_since_last_movement >= (inact.escalate_threshold_days || 14)
                    ? 'border-rose-500/40 bg-rose-500/5'
                    : inact.days_since_last_movement >= (inact.threshold_days || 7)
                    ? 'border-amber-500/40 bg-amber-500/5'
                    : 'border-emerald-500/30 bg-emerald-500/5'
                }`}>
                  <div className="flex items-center gap-2 text-sm">
                    {inact.days_since_last_movement == null ? (
                      <Clock className="h-4 w-4" />
                    ) : (
                      <AlertTriangle className="h-4 w-4" />
                    )}
                    <span>
                      Último movimiento operativo:{' '}
                      {inact.last_movement ? (
                        <>
                          <b>{inact.last_movement.type}</b>
                          {inact.last_movement.event_name ? ` · ${inact.last_movement.event_name}` : ''}
                          {' '}—{' '}
                          {inact.days_since_last_movement != null
                            ? `hace ${inact.days_since_last_movement} día(s)`
                            : (inact.last_movement.created_at || '—')}
                        </>
                      ) : (
                        <i>Nunca</i>
                      )}
                    </span>
                  </div>
                </div>

                {/* Enabled toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Alerta activa</Label>
                    <p className="text-xs text-muted-foreground">
                      Si está apagada, el cron diario no envía nada.
                    </p>
                  </div>
                  <Switch
                    checked={inact.enabled}
                    onCheckedChange={(v) => setInact((s) => ({ ...s, enabled: v }))}
                    data-testid="inact-enabled"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-sm">Umbral de alerta (días)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={inact.threshold_days}
                      onChange={(e) => setInact((s) => ({ ...s, threshold_days: parseInt(e.target.value) || 7 }))}
                      data-testid="inact-threshold"
                      disabled={!inact.enabled}
                    />
                    <p className="text-[11px] text-muted-foreground">Recomendado: 7</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Escalar a URGENTE después de (días)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={inact.escalate_threshold_days}
                      onChange={(e) => setInact((s) => ({ ...s, escalate_threshold_days: parseInt(e.target.value) || 14 }))}
                      data-testid="inact-escalate"
                      disabled={!inact.enabled}
                    />
                    <p className="text-[11px] text-muted-foreground">El correo cambia a rojo y dice URGENTE</p>
                  </div>
                </div>

                {/* Extra emails */}
                <div className="space-y-2">
                  <Label className="text-sm">Correos adicionales (no requieren ser usuarios)</Label>
                  <p className="text-[11px] text-muted-foreground -mt-1">
                    Ejemplo: el correo del director, una bandeja compartida, un email de auditoría externa.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="ejemplo@empresa.com"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                      data-testid="inact-new-email"
                      disabled={!inact.enabled}
                    />
                    <Button type="button" onClick={addEmail} variant="outline" disabled={!inact.enabled} data-testid="inact-add-email">
                      <Plus className="h-4 w-4 mr-1" /> Agregar
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(inact.extra_emails || []).map((em) => (
                      <Badge key={em} variant="secondary" className="gap-1.5 pr-1.5">
                        <Mail className="h-3 w-3" />
                        {em}
                        <button
                          onClick={() => removeEmail(em)}
                          className="ml-1 rounded-sm hover:bg-rose-500/20 p-0.5"
                          aria-label={`Quitar ${em}`}
                          data-testid={`inact-remove-${em}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                    {(inact.extra_emails || []).length === 0 && (
                      <p className="text-xs text-muted-foreground italic">Sin correos extra. Solo se notifica a admins/superadmins activos.</p>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-wrap pt-2 border-t">
                  <Button onClick={saveInactivity} disabled={inactSaving} data-testid="inact-save">
                    {inactSaving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Guardar
                  </Button>
                  <Button onClick={testInactivity} variant="outline" disabled={inactTesting} data-testid="inact-test">
                    {inactTesting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                    Probar (enviar ahora)
                  </Button>
                  <Button onClick={loadInactivity} variant="ghost" size="sm">
                    <RefreshCw className="h-3 w-3 mr-1" /> Recargar
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="perdidos" className="mt-4 space-y-4">
        {/* v5.4.17 — Probar PDF de Equipos Perdidos */}
        <Card className="border-red-500/30" data-testid="lost-overdue-pdf-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-5 w-5 text-red-500" />
              Probar PDF — Equipos Perdidos
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Envía un PDF de muestra del reporte <b>&quot;Equipos Perdidos&quot;</b> (rediseñado en v5.4.17
              con branding CM Idiomas, panel de estadísticas y columna &quot;No. Control del Evento&quot;) al
              correo que indiques para validar el diseño y la entrega del adjunto, sin esperar al
              cron job.
            </p>
          </CardHeader>
          <CardContent>
            <Button
              onClick={sendTestLostOverduePdf}
              disabled={lostPdfTesting}
              variant="outline"
              data-testid="test-lost-overdue-pdf-btn"
            >
              {lostPdfTesting
                ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                : <Mail className="h-4 w-4 mr-2" />}
              Enviar PDF de prueba
            </Button>
          </CardContent>
        </Card>

        {/* v5.4.18 — Equipo perdido (lost-overdue) */}
        <Card data-testid="lost-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Equipo Perdido vencido (a Recuperar &gt; N días)
              {lost && (
                <Badge variant={lost.enabled ? 'default' : 'secondary'} className="ml-2">
                  {lost.enabled ? 'Activo' : 'Apagado'}
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Alerta cuando una unidad lleva más de N días en estado &quot;Perdido a Recuperar&quot;.
              Adjunta el PDF rediseñado con branding CM Idiomas.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {!lost ? (
              <div className="text-sm text-muted-foreground">Cargando...</div>
            ) : (
              <>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <Label className="text-sm font-medium">Alertas activas</Label>
                  <Switch
                    checked={!!lost.enabled}
                    disabled={lostSaving}
                    onCheckedChange={(v) => saveLost({ enabled: v })}
                    data-testid="lost-enabled-toggle"
                  />
                </div>
                <div>
                  <Label className="text-xs">Umbral global (días)</Label>
                  <Input
                    type="number" min="1" max="120"
                    value={lost.threshold_days ?? 15}
                    disabled={lostSaving || !lost.enabled}
                    onChange={(e) => setLost({ ...lost, threshold_days: parseInt(e.target.value || '1') })}
                    onBlur={() => saveLost({ threshold_days: lost.threshold_days })}
                    data-testid="lost-threshold-days"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Default: 15 días. Umbrales por categoría siguen disponibles en Dashboard → Personalizar.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="movimientos" className="mt-4 space-y-4">
        {/* v5.4.18 — Notificaciones de Movimientos / Transiciones (la principal causante del spam) */}
        <Card className="border-blue-500/30" data-testid="transitions-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Bell className="h-5 w-5 text-blue-500" />
              Notificaciones de Movimientos (transiciones)
              {trans && (
                <Badge variant={trans.enabled ? 'default' : 'secondary'} className="ml-2">
                  {trans.enabled ? 'Activo' : 'Apagado'}
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Controla los correos / notificaciones in-app que se disparan en CADA paso del workflow
              (Entregado, Retorno, Completado). Si los apagas aquí, dejarás de recibir un correo por
              cada movimiento o evento que se registre.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {!trans ? (
              <div className="text-sm text-muted-foreground">Cargando...</div>
            ) : (
              <>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label className="text-sm font-medium">Notificaciones activas</Label>
                    <p className="text-xs text-muted-foreground">
                      Si está apagado, no se envían correos ni notifs in-app para transiciones de movimientos.
                    </p>
                  </div>
                  <Switch
                    checked={!!trans.enabled}
                    disabled={transSaving}
                    onCheckedChange={(v) => saveTransitions({ enabled: v })}
                    data-testid="transitions-enabled-toggle"
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label className="text-sm font-medium">Enviar también por correo</Label>
                    <p className="text-xs text-muted-foreground">
                      Si lo apagas, solo verás notificaciones in-app (campana). El correo se omite.
                    </p>
                  </div>
                  <Switch
                    checked={!!trans.send_email}
                    disabled={transSaving || !trans.enabled}
                    onCheckedChange={(v) => saveTransitions({ send_email: v })}
                    data-testid="transitions-email-toggle"
                  />
                </div>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label className="text-sm font-medium">Agrupar en digest diario (recomendado)</Label>
                    <p className="text-xs text-muted-foreground">
                      Acumula las transiciones del día y envía UN solo correo a la hora configurada (lugar habitual: 18:00).
                      Reduce el volumen de correos sin perder visibilidad. &quot;Completado&quot; siempre se envía al momento.
                    </p>
                  </div>
                  <Switch
                    checked={!!(trans.digest && trans.digest.enabled)}
                    disabled={transSaving || !trans.enabled}
                    onCheckedChange={(v) => saveTransitions({
                      digest: { ...(trans.digest || { hour: 18, steps: ['entregado', 'retorno'] }), enabled: v }
                    })}
                    data-testid="transitions-digest-toggle"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="retornos" className="mt-4 space-y-4">
        {/* v5.4.18 — Retornos pendientes (equipo no devuelto) */}
        <Card data-testid="loan-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock className="h-5 w-5 text-orange-500" />
              Retornos pendientes / Equipo no devuelto
              {loan && (
                <Badge variant={loan.enabled ? 'default' : 'secondary'} className="ml-2">
                  {loan.enabled ? 'Activo' : 'Apagado'}
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Correo diario a admins cuando un equipo sigue en la calle más allá del umbral configurado.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {!loan ? (
              <div className="text-sm text-muted-foreground">Cargando...</div>
            ) : (
              <>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <Label className="text-sm font-medium">Alertas activas</Label>
                  <Switch
                    checked={!!loan.enabled}
                    disabled={loanSaving}
                    onCheckedChange={(v) => saveLoan({ enabled: v })}
                    data-testid="loan-enabled-toggle"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Días antes de vencer</Label>
                    <Input
                      type="number" min="0" max="30"
                      value={loan.warning_days ?? 2}
                      disabled={loanSaving || !loan.enabled}
                      onChange={(e) => setLoan({ ...loan, warning_days: parseInt(e.target.value || '0') })}
                      onBlur={() => saveLoan({ warning_days: loan.warning_days })}
                      data-testid="loan-warning-days"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Días vencido (crítico)</Label>
                    <Input
                      type="number" min="1" max="60"
                      value={loan.threshold_days ?? 7}
                      disabled={loanSaving || !loan.enabled}
                      onChange={(e) => setLoan({ ...loan, threshold_days: parseInt(e.target.value || '1') })}
                      onBlur={() => saveLoan({ threshold_days: loan.threshold_days })}
                      data-testid="loan-threshold-days"
                    />
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* v5.4.27 — Return reminders (creator → directors escalation) */}
        <Card data-testid="return-reminder-card" className="border-amber-500/40">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="h-5 w-5 text-amber-500" />
              Recordatorio y escalación de retorno
              {returnReminder && (
                <Badge variant={returnReminder.enabled ? 'default' : 'secondary'} className="ml-2">
                  {returnReminder.enabled ? 'Activo' : 'Apagado'}
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Al creador del movimiento en N días. Si no responde, escala a los directores en M días.
              Un correo por movimiento (no se repite).
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {!returnReminder ? (
              <div className="text-sm text-muted-foreground">Cargando...</div>
            ) : (
              <>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <Label className="text-sm font-medium">Cadena activa</Label>
                  <Switch
                    checked={!!returnReminder.enabled}
                    disabled={returnReminderSaving}
                    onCheckedChange={(v) => saveReturnReminder({ enabled: v })}
                    data-testid="return-reminder-enabled"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Días para avisar al creador</Label>
                    <Input
                      type="number" min="1" max="365"
                      value={returnReminder.creator_days ?? 7}
                      disabled={returnReminderSaving || !returnReminder.enabled}
                      onChange={(e) => setReturnReminder({ ...returnReminder, creator_days: parseInt(e.target.value || '7', 10) })}
                      onBlur={() => saveReturnReminder({ creator_days: returnReminder.creator_days })}
                      data-testid="return-reminder-creator-days"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Días para escalar a directores</Label>
                    <Input
                      type="number" min="1" max="365"
                      value={returnReminder.director_days ?? 10}
                      disabled={returnReminderSaving || !returnReminder.enabled}
                      onChange={(e) => setReturnReminder({ ...returnReminder, director_days: parseInt(e.target.value || '10', 10) })}
                      onBlur={() => saveReturnReminder({ director_days: returnReminder.director_days })}
                      data-testid="return-reminder-director-days"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={runReturnRemindersNow}
                    disabled={returnReminderRunning || !returnReminder.enabled}
                    data-testid="return-reminder-run-now"
                  >
                    {returnReminderRunning ? (
                      <RefreshCw className="h-3 w-3 mr-2 animate-spin" />
                    ) : (
                      <Mail className="h-3 w-3 mr-2" />
                    )}
                    Ejecutar escaneo ahora
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground">
                  El día {returnReminder.creator_days} después de la salida, se envía correo al creador si aún no hay retorno.
                  El día {returnReminder.director_days} sin retorno, se notifica a admins/superadmins.
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* v5.4.18 — Bomberazo */}
          </TabsContent>

          <TabsContent value="bomberazos" className="mt-4 space-y-4">
        <Card data-testid="bomberazo-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Bomberazos (salidas sin revisión)
              {bomberazo && (
                <Badge variant={bomberazo.enabled ? 'default' : 'secondary'} className="ml-2">
                  {bomberazo.enabled ? 'Activo' : 'Apagado'}
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Notifica a admins cuando se crea una salida marcada como bomberazo. Se agrupa en digest cada 5 min.
            </p>
          </CardHeader>
          <CardContent>
            {!bomberazo ? (
              <div className="text-sm text-muted-foreground">Cargando...</div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label className="text-sm font-medium">Notificación inmediata</Label>
                    <p className="text-xs text-muted-foreground">
                      Avisa a admins en el momento que se crea una salida marcada como bomberazo (correo + notif in-app, agrupado en digest cada 5 min).
                    </p>
                  </div>
                  <Switch
                    checked={!!bomberazo.enabled}
                    disabled={bomberazoSaving}
                    onCheckedChange={(v) => saveBomberazo({ enabled: v })}
                    data-testid="bomberazo-enabled-toggle"
                  />
                </div>

                {/* v5.4.22 — Daily bomberazo report */}
                <div className="p-3 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium">Reporte diario consolidado</Label>
                      <p className="text-xs text-muted-foreground">
                        PDF resumen enviado todos los días a una hora fija con los bomberazos de las últimas 24 h.
                      </p>
                    </div>
                    <Switch
                      checked={!!bomberazo.daily_report_enabled}
                      disabled={bomberazoSaving}
                      onCheckedChange={(v) => saveBomberazo({ daily_report_enabled: v })}
                      data-testid="bomberazo-daily-toggle"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Hora de envío (CDMX)</Label>
                      <Input
                        type="time"
                        value={bomberazo.daily_report_time || '08:00'}
                        disabled={bomberazoSaving || !bomberazo.daily_report_enabled}
                        onChange={(e) => setBomberazo({ ...bomberazo, daily_report_time: e.target.value })}
                        onBlur={() => saveBomberazo({ daily_report_time: bomberazo.daily_report_time })}
                        data-testid="bomberazo-daily-time"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Destinatarios adicionales del reporte diario</Label>
                    <p className="text-xs text-muted-foreground">Default: admins activos. Aquí añade correos extra.</p>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="email"
                        placeholder="otro@correo.com"
                        disabled={bomberazoSaving || !bomberazo.daily_report_enabled}
                        data-testid="bomberazo-daily-new-email"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim().includes('@')) {
                            const next = [...(bomberazo.daily_report_recipients || [])];
                            const em = e.target.value.trim();
                            if (!next.includes(em)) next.push(em);
                            saveBomberazo({ daily_report_recipients: next });
                            e.target.value = '';
                          }
                        }}
                      />
                    </div>
                    {(bomberazo.daily_report_recipients || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {bomberazo.daily_report_recipients.map((em) => (
                          <Badge key={em} variant="secondary" className="pr-1" data-testid={`bomberazo-daily-${em}`}>
                            {em}
                            <button
                              type="button"
                              className="ml-1 hover:bg-muted rounded-sm p-0.5"
                              onClick={() => saveBomberazo({
                                daily_report_recipients: bomberazo.daily_report_recipients.filter((x) => x !== em)
                              })}
                              data-testid={`bomberazo-daily-remove-${em}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* v5.4.18 — KPI thresholds */}
          </TabsContent>

          <TabsContent value="kpi" className="mt-4 space-y-4">
        <Card data-testid="kpi-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="h-5 w-5 text-purple-500" />
              KPIs ejecutivos (alertas proactivas)
              {kpi && (
                <Badge variant={kpi.enabled ? 'default' : 'secondary'} className="ml-2">
                  {kpi.enabled ? 'Activo' : 'Apagado'}
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Alerta cuando el tiempo promedio, la tasa de cierre o el % de notas cruzan un umbral configurable.
            </p>
          </CardHeader>
          <CardContent>
            {!kpi ? (
              <div className="text-sm text-muted-foreground">Cargando...</div>
            ) : (
              <>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <Label className="text-sm font-medium">Alertas activas</Label>
                  <Switch
                    checked={!!kpi.enabled}
                    disabled={kpiSaving}
                    onCheckedChange={(v) => saveKpi({ enabled: v })}
                    data-testid="kpi-enabled-toggle"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="huerfanas" className="mt-4 space-y-4">
        {/* v5.4.21 — Orphan units alerts (Mondays 09:00 CDMX) */}
        <Card data-testid="orphan-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              Unidades huérfanas (chequeo semanal)
              {orphan && (
                <Badge variant={orphan.enabled ? 'default' : 'secondary'} className="ml-2">
                  {orphan.enabled ? 'Activo' : 'Apagado'}
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Cada <b>lunes a las 09:00 CDMX</b> el sistema detecta unidades en estado &quot;perdido&quot; cuyo
              movimiento de origen fue eliminado (huérfanas) y envía un correo a admins para que las
              recuperen o den de baja desde <code>/debugger → Salud del Inventario</code>.
              No se envía nada si no hay huérfanas.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {!orphan ? (
              <div className="text-sm text-muted-foreground">Cargando...</div>
            ) : (
              <>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <div>
                    <Label className="text-sm font-medium">Alertas activas</Label>
                    <p className="text-xs text-muted-foreground">
                      Si lo apagas, el chequeo semanal sigue corriendo (para mantener el conteo en el dashboard) pero NO envía correo.
                    </p>
                  </div>
                  <Switch
                    checked={!!orphan.enabled}
                    disabled={orphanSaving}
                    onCheckedChange={(v) => saveOrphan({ enabled: v })}
                    data-testid="orphan-enabled-toggle"
                  />
                </div>

                <div className="p-3 border rounded-lg space-y-2">
                  <Label className="text-sm font-medium">Destinatarios adicionales</Label>
                  <p className="text-xs text-muted-foreground">
                    Por default se envía a todos los admins/superadmins activos. Aquí puedes añadir correos extra
                    (ej. responsables de bodega externos) sin que sean usuarios del sistema.
                  </p>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="otro@correo.com"
                      value={newOrphanEmail}
                      onChange={(e) => setNewOrphanEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOrphanEmail(); } }}
                      disabled={orphanSaving || !orphan.enabled}
                      data-testid="orphan-new-email"
                    />
                    <Button
                      onClick={addOrphanEmail}
                      disabled={orphanSaving || !orphan.enabled || !newOrphanEmail.trim()}
                      data-testid="orphan-add-email"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {(orphan.extra_emails || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      {orphan.extra_emails.map((em) => (
                        <Badge key={em} variant="secondary" className="pr-1" data-testid={`orphan-extra-${em}`}>
                          {em}
                          <button
                            type="button"
                            className="ml-1 hover:bg-muted rounded-sm p-0.5"
                            onClick={() => removeOrphanEmail(em)}
                            data-testid={`orphan-remove-${em}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Other notification centers (links) — only items not yet inlined */}
          </TabsContent>

          <TabsContent value="briefing" className="mt-4 space-y-4">
        {/* v5.4.22 — Weekly briefing config (was only in /email-schedule) */}
        <Card data-testid="briefing-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <FileText className="h-5 w-5 text-blue-500" />
              Briefing Semanal
              {briefing && (
                <Badge variant={briefing.enabled ? 'default' : 'secondary'} className="ml-2">
                  {briefing.enabled ? 'Activo' : 'Apagado'}
                </Badge>
              )}
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Resumen semanal de la operación enviado a admins y a destinatarios extra. Incluye reservaciones,
              mantenimientos, movimientos y estadísticas. Por defecto se manda los <b>lunes 07:00 CDMX</b>.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {!briefing ? (
              <div className="text-sm text-muted-foreground">Cargando...</div>
            ) : (
              <>
                <div className="flex items-center justify-between p-3 border rounded-lg">
                  <Label className="text-sm font-medium">Briefing activo</Label>
                  <Switch
                    checked={!!briefing.enabled}
                    disabled={briefingSaving}
                    onCheckedChange={(v) => saveBriefing({ enabled: v })}
                    data-testid="briefing-enabled-toggle"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Día de la semana</Label>
                    <select
                      value={briefing.day_of_week || 'mon'}
                      disabled={briefingSaving || !briefing.enabled}
                      onChange={(e) => saveBriefing({ day_of_week: e.target.value })}
                      className="w-full mt-1 h-9 px-3 rounded-md border bg-background text-sm"
                      data-testid="briefing-day"
                    >
                      <option value="mon">Lunes</option>
                      <option value="tue">Martes</option>
                      <option value="wed">Miércoles</option>
                      <option value="thu">Jueves</option>
                      <option value="fri">Viernes</option>
                      <option value="sat">Sábado</option>
                      <option value="sun">Domingo</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Hora (CDMX)</Label>
                    <Input
                      type="time"
                      value={briefing.time_of_day || '07:00'}
                      disabled={briefingSaving || !briefing.enabled}
                      onChange={(e) => setBriefing({ ...briefing, time_of_day: e.target.value })}
                      onBlur={() => saveBriefing({ time_of_day: briefing.time_of_day })}
                      data-testid="briefing-time"
                    />
                  </div>
                </div>

                <div className="p-3 border rounded-lg space-y-2">
                  <Label className="text-sm font-medium">Secciones incluidas</Label>
                  {[
                    { key: 'include_reservations', label: 'Reservaciones' },
                    { key: 'include_maintenance',  label: 'Mantenimientos' },
                    { key: 'include_movements',    label: 'Movimientos' },
                    { key: 'include_stats',        label: 'Estadísticas' },
                  ].map((s) => (
                    <div key={s.key} className="flex items-center justify-between">
                      <span className="text-sm">{s.label}</span>
                      <Switch
                        checked={briefing[s.key] !== false}
                        disabled={briefingSaving || !briefing.enabled}
                        onCheckedChange={(v) => saveBriefing({ [s.key]: v })}
                        data-testid={`briefing-${s.key}`}
                      />
                    </div>
                  ))}
                </div>

                <div className="p-3 border rounded-lg space-y-2">
                  <Label className="text-sm font-medium">Destinatarios adicionales</Label>
                  <p className="text-xs text-muted-foreground">Default: admins activos. Aquí añade correos extra.</p>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="otro@correo.com"
                      value={newBriefingEmail}
                      onChange={(e) => setNewBriefingEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addBriefingEmail(); } }}
                      disabled={briefingSaving || !briefing.enabled}
                      data-testid="briefing-new-email"
                    />
                    <Button
                      onClick={addBriefingEmail}
                      disabled={briefingSaving || !briefing.enabled || !newBriefingEmail.trim()}
                      data-testid="briefing-add-email"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {(briefing.extra_recipients || []).length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      {briefing.extra_recipients.map((em) => (
                        <Badge key={em} variant="secondary" className="pr-1" data-testid={`briefing-extra-${em}`}>
                          {em}
                          <button
                            type="button"
                            className="ml-1 hover:bg-muted rounded-sm p-0.5"
                            onClick={() => removeBriefingEmail(em)}
                            data-testid={`briefing-remove-${em}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={sendBriefingNow}
                    disabled={briefingSending}
                    data-testid="briefing-send-now"
                  >
                    {briefingSending ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                    Enviar ahora
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="ejecutivo" className="mt-4 space-y-4">
            {/* v5.4.27 — Executive Inactivity Threshold */}
            <Card data-testid="inactivity-threshold-card" className="border-amber-500/40">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="h-5 w-5 text-amber-500" />
                  Umbral de inactividad para el reporte
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Marca en <b>amarillo</b> los usuarios sin login ni actividad en los últimos N días
                  para revisar accesos huérfanos. Aparece en el Excel (hoja Resumen) y el PDF Ejecutivo (banner + tabla).
                </p>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-3">
                  <div className="flex-1 max-w-xs">
                    <Label className="text-xs">Días sin actividad para considerar inactivo</Label>
                    <Input
                      type="number" min="1" max="365"
                      value={inactivityCfg?.threshold_days ?? 14}
                      disabled={inactivitySaving}
                      onChange={(e) => setInactivityCfg({ ...(inactivityCfg || {}), threshold_days: parseInt(e.target.value || '14', 10) })}
                      onBlur={() => saveExecInactivity({ threshold_days: inactivityCfg?.threshold_days })}
                      data-testid="inactivity-threshold-days"
                    />
                  </div>
                  <span className="text-sm text-muted-foreground pb-2">días</span>
                </div>
              </CardContent>
            </Card>

            {/* v5.4.26 — Executive Report Weekly Schedule */}
            <Card className="border-primary/40" data-testid="executive-report-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Reporte Ejecutivo Semanal (PDF por correo)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg bg-muted/40 p-3 text-sm">
                  Envía automáticamente el <b>PDF Ejecutivo de una página</b> (KPIs por usuario,
                  gráfica top-10 y tabla detallada) a los correos configurados el día y hora
                  seleccionados, todas las semanas. Idealmente los <b>lunes por la mañana</b>
                  para que dirección arranque la semana con contexto.
                </div>

                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <Label className="text-sm font-medium">Envío automático</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Cuando esté activo, se enviará al grupo configurado cada semana.
                    </p>
                  </div>
                  <Switch
                    checked={!!execReport?.enabled}
                    onCheckedChange={(v) => saveExecReport({ enabled: v })}
                    disabled={execReportSaving}
                    data-testid="exec-report-enabled"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-sm">Día de la semana</Label>
                    <select
                      className="mt-1 w-full h-9 rounded-md border bg-background px-3 text-sm"
                      value={execReport?.day_of_week || 'mon'}
                      onChange={(e) => saveExecReport({ day_of_week: e.target.value })}
                      disabled={execReportSaving || !execReport?.enabled}
                      data-testid="exec-report-day"
                    >
                      <option value="mon">Lunes</option>
                      <option value="tue">Martes</option>
                      <option value="wed">Miércoles</option>
                      <option value="thu">Jueves</option>
                      <option value="fri">Viernes</option>
                      <option value="sat">Sábado</option>
                      <option value="sun">Domingo</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm">Hora (CDMX)</Label>
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={execReport?.hour ?? 8}
                      onChange={(e) => saveExecReport({ hour: parseInt(e.target.value || '0', 10) })}
                      disabled={execReportSaving || !execReport?.enabled}
                      className="mt-1"
                      data-testid="exec-report-hour"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Ventana (días)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={execReport?.days_lookback ?? 30}
                      onChange={(e) => saveExecReport({ days_lookback: parseInt(e.target.value || '30', 10) })}
                      disabled={execReportSaving || !execReport?.enabled}
                      className="mt-1"
                      data-testid="exec-report-days"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Reporte cubre los últimos N días</p>
                  </div>
                </div>

                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <Label className="text-sm font-medium">Comparación (mes vs mes)</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Incluir flechas ↑↓ vs periodo anterior de la misma duración.
                    </p>
                  </div>
                  <Switch
                    checked={!!execReport?.include_comparison}
                    onCheckedChange={(v) => saveExecReport({ include_comparison: v })}
                    disabled={execReportSaving || !execReport?.enabled}
                    data-testid="exec-report-compare"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Destinatarios</Label>
                  <div className="flex gap-2 mb-3">
                    <Input
                      type="email"
                      placeholder="director@cmidiomas.com"
                      value={newExecRecipient}
                      onChange={(e) => setNewExecRecipient(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && addExecRecipient()}
                      disabled={execReportSaving}
                      data-testid="exec-report-new-recipient"
                    />
                    <Button
                      onClick={addExecRecipient}
                      disabled={execReportSaving || !newExecRecipient.trim()}
                      variant="outline"
                      data-testid="exec-report-add-recipient"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Agregar
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(execReport?.recipients || []).length === 0 ? (
                      <p className="text-xs text-muted-foreground">
                        Aún no hay destinatarios. Agrega al menos uno para poder activar el envío.
                      </p>
                    ) : (
                      (execReport?.recipients || []).map((em) => (
                        <Badge
                          key={em}
                          variant="secondary"
                          className="pl-3 pr-1 py-1 flex items-center gap-2"
                          data-testid={`exec-report-recipient-${em}`}
                        >
                          {em}
                          <button
                            onClick={() => removeExecRecipient(em)}
                            className="hover:bg-destructive/20 rounded p-0.5"
                            title="Eliminar"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-3 border-t">
                  <Button
                    onClick={sendExecReportNow}
                    disabled={execReportSending || !(execReport?.recipients || []).length}
                    variant="outline"
                    data-testid="exec-report-send-now"
                  >
                    {execReportSending ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="h-4 w-4 mr-2" />
                    )}
                    Enviar ahora
                  </Button>
                  {execReport?.enabled && (execReport?.recipients || []).length > 0 && (
                    <p className="text-xs text-muted-foreground self-center ml-2">
                      Próximo envío: {({mon:'Lunes',tue:'Martes',wed:'Miércoles',thu:'Jueves',fri:'Viernes',sat:'Sábado',sun:'Domingo'})[execReport.day_of_week]} a las {String(execReport.hour).padStart(2,'0')}:00 CDMX
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>


          <TabsContent value="otros" className="mt-4 space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Atajos relacionados</CardTitle>
            <p className="text-xs text-muted-foreground">
              Pantallas auxiliares para configurar destinatarios avanzados, plantillas y herramientas de mantenimiento.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                { label: 'Briefing semanal (per-usuario)', path: '/email-schedule', tab: null },
                { label: 'Unidades perdidas huérfanas', path: '/debugger', tab: 'Salud del Inventario' },
                { label: 'Plantillas de correo de transiciones', path: '/dashboard', tab: 'Personalizar' },
              ].map((it) => (
                <button
                  key={it.label}
                  onClick={() => navigate(it.path)}
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/40 text-left transition-colors"
                  data-testid={`shortcut-${it.path.replace('/', '')}`}
                >
                  <div>
                    <p className="text-sm font-medium">{it.label}</p>
                    <p className="text-xs text-muted-foreground">
                      <code>{it.path}</code>
                      {it.tab && <> → tab <b>{it.tab}</b></>}
                    </p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
          </TabsContent>

          <TabsContent value="destinatarios" className="mt-4">
            <RecipientsPanel api={api} />
          </TabsContent>

          <TabsContent value="diagnostico" className="mt-4">
            <DiagnosticPanel api={api} />
          </TabsContent>

          <TabsContent value="historial" className="mt-4">
            <EmailLogPanel api={api} />
          </TabsContent>
        </Tabs>
      </div>
  );
}
